import type { useGT } from 'gt-react';
import { toast } from 'sonner';
import type { DeleteVendorPortalFilesOutput } from '@erp/rpc';

type Translate = ReturnType<typeof useGT>;

/** Toast what a delete did: what went, what was kept in use, and what failed. */
export function reportDeleteResult(
  result: DeleteVendorPortalFilesOutput,
  gt: Translate,
) {
  if (result.deleted.length > 0) {
    toast.success(
      gt('{count, plural, one {Deleted # file} other {Deleted # files}}', {
        context: 'Toast after vendor portal files were permanently deleted',
        count: result.deleted.length,
      }),
    );
  }
  const [onlyKept] = result.inUse;
  // Name what still uses the kept files (database tables), so a surprising "kept" can
  // be traced without opening the network panel.
  const usedBy = gt('Still used by: {tables}', {
    context:
      'Toast detail naming the database tables that still use the vendor portal files that were not deleted',
    tables: [...new Set(result.inUse.flatMap((entry) => entry.usedBy))].join(
      ', ',
    ),
  });
  if (result.inUse.length === 1 && onlyKept) {
    toast.warning(
      gt('Kept {fileName}: other records still use it', {
        context:
          'Toast when a vendor portal file was not deleted because other data references it',
        fileName: onlyKept.fileName,
      }),
      { description: usedBy },
    );
  } else if (result.inUse.length > 1) {
    toast.warning(
      gt('Kept {count} files: other records still use them', {
        context:
          'Toast when several vendor portal files were not deleted because other data references them',
        count: result.inUse.length,
      }),
      { description: usedBy },
    );
  }
  if (result.failed.length > 0) {
    toast.error(
      gt(
        '{count, plural, one {Could not delete # file. Try again.} other {Could not delete # files. Try again.}}',
        {
          context: 'Toast when deleting some vendor portal files failed',
          count: result.failed.length,
        },
      ),
    );
  }
}
